import { DOMParser, XMLSerializer } from 'xmldom';
import constants from './../../Constants';
import {
    isMedia,
    getMediaTargetId,
    getMediaPlaceholderType,
    generateFileUrlForLogicalName,
    isNewFormatImage,
} from './MediaModels';
import { isPDFPanel } from './PDFPanelModel';
import { isRubricBlock } from './RubricBlockModel';
import themeServices from '../../../themes/ThemeServices';
import { t } from '../../../services/i18nService';
import marketplacePropertiesService from '../../../services/MarketplacePropertiesService';
import objectTypes from '../../../constants/ObjectTypes';

const serializer = new XMLSerializer();
export const INLINE_INTERACTIONS = ['inlineChoiceInteraction', 'textEntryInteraction'];

/**
 * Updates the itemBody element replacing the src attributes in "static" img elements to be treated as plain HTML
 * @param {Element} itemBody
 * @param assessmentRunId - optional
 */
export function replaceImageUrlsForImgTags(itemBody, assessmentRunId) {
    const imgTags = itemBody.getElementsByTagName('img');
    for (const imgTag of Array.from(imgTags)) {
        const logicalName = imgTag.getAttribute('data-logicalname');
        if (logicalName) {
            const newSrcUrl = generateFileUrlForLogicalName(logicalName, undefined, assessmentRunId, 'image');
            imgTag.setAttribute('src', newSrcUrl);
        }
    }
}

/**
 * Generates an id for a placeholder element based on question and response identifiers.
 *
 * @param {string} questionIdentifier
 * @param {string} responseIdentifier
 * @returns {string}
 */
export function getPlaceholderId(questionIdentifier, responseIdentifier) {
    return `${questionIdentifier}_${responseIdentifier}`;
}

/**
 * Checks if an interaction element is of a type meant to be displayed inline.
 *
 * @param {Element} interactionElement
 * @returns {boolean}
 */
export function isInlineInteraction(interactionElement) {
    return INLINE_INTERACTIONS.indexOf(interactionElement.localName) > -1;
}

export function createPlaceholderElement(interactionElement, questionIdentifier, forceDiv = false) {
    const responseIdentifier = interactionElement.getAttribute('responseIdentifier');
    const nodeType = !forceDiv && isInlineInteraction(interactionElement) ? 'span' : 'div';
    const placeholder = interactionElement.ownerDocument.createElement(nodeType);
    placeholder.textContent = 'placeholder';
    placeholder.setAttribute('id', getPlaceholderId(questionIdentifier, responseIdentifier));
    placeholder.setAttribute('class', 'interaction-container');

    return placeholder;
}

/**
 * Replaces an interaction element with a place holder element with questionIdentifier as id.
 *
 * @param {Element} interactionElement
 * @param {string} questionIdentifier
 */
export function replaceInteractionWithPlaceholder(interactionElement, questionIdentifier) {
    interactionElement.parentNode.replaceChild(
        createPlaceholderElement(interactionElement, questionIdentifier),
        interactionElement
    );
}

/**
 * Replaces a media element with a place holder element with logicalName as id.
 *
 * @param {Element} mediaElement
 * @param {string} questionIdentifier
 */
export function replaceMediaWithPlaceholder(mediaElement, questionIdentifier) {
    const doc = mediaElement.ownerDocument;
    const nodeType = getMediaPlaceholderType(mediaElement);
    let wrappedChildNodes;
    if (isNewFormatImage(mediaElement) && mediaElement.childNodes) {
        // img tags can by mistake from editor wrap content. Extract content and insert after placeholder
        wrappedChildNodes = mediaElement.childNodes;
    }
    const placeholder = doc.createElement(nodeType);
    placeholder.textContent = 'placeholder';
    placeholder.setAttribute('id', getMediaTargetId(questionIdentifier, mediaElement));
    placeholder.setAttribute('class', 'qti-renderer-media-wrapper');
    mediaElement.parentNode.replaceChild(placeholder, mediaElement);
    if (wrappedChildNodes) {
        Array.from(wrappedChildNodes).reverse().forEach((node) => {
            // Reverse array as there is no method for insertAfter
            mediaElement.parentNode.insertBefore(node, placeholder.nextSibling);
        });
    }
}

/**
 * Checks if a DOM Element is an interaction.
 *
 * @param {Element} element
 * @returns {boolean}
 */
export function isInteraction(element) {
    return element.nodeType === 1 && element.hasAttribute('responseIdentifier');
}

export function isLanguageControlledElementType(nodeName) {
    return constants.languageControlledTags.indexOf(nodeName) !== -1;
}

export function isMediaElement(element) {
    return element.nodeName === 'div' && element.getAttribute('class').includes('ia-media');
}

export function isInsperaNamespacedStyleBodyElement(element) {
    return !isInteraction(element) && element.nodeType === 1 && element.hasAttribute('inspera:style');
}

/**
 * Utility function to check if the element is in the desired language
 * @param {Element} element
 * @param {string} requestedLanguage
 * @param {string} defaultLanguage
 * @returns {boolean}
 */
export function isInCorrectLanguage(element, requestedLanguage, defaultLanguage) {
    if (!isLanguageControlledElementType(element.nodeName) && !isMediaElement(element)) {
        return true;
    }

    const elementLanguage = element.getAttribute('lang');
    const useDefaultLanguage = requestedLanguage === undefined || requestedLanguage === defaultLanguage;
    const isInDefaultLanguage = !elementLanguage || elementLanguage === defaultLanguage;

    return (elementLanguage === requestedLanguage) || (useDefaultLanguage && isInDefaultLanguage);
}

/**
 * Empty inline elements get closed by XMLSerializer and can cause wrapping
 * of the following html when inserted into the DOM using dangerouslyInsertInnerHtml
 * @param {Element} element
 * @return {boolean}
 */
export function isInvalidEmptyInlineElement(element) {
    return element.nodeType === 1
        && constants.inlineElementsThatCannotBeEmpty.indexOf(element.localName) > -1
        && (!element.firstChild || isInvalidEmptyInlineElement(element.firstChild));
}

/**
 * The editor sometimes leaves damaged interactions in the html...
 * @param {Element} element
 * @return {boolean}
 */
export function isInvalidHtmlMarkup(element) {
    return !!(
        element.hasAttribute('placeholder-object-id')
        && element.localName.indexOf('interaction') !== -1
    );
}

export function removeHiddenStyle(element) {
    const styleStr = element.getAttribute('style') || '';
    if (!styleStr) {
        return undefined;
    }

    const replaced = styleStr
        .replace(/display:(\s)?none;/, '')
        .trim();
    if (replaced.length === 0) {
        return element.removeAttribute('style');
    }

    return element.setAttribute('style', replaced);
}

export function applyInsperaNamespaceStyle(element) {
    const insperaNamespaceStyle = element.getAttribute('inspera:style');
    element.removeAttribute('inspera:style');
    element.setAttribute('style', insperaNamespaceStyle);
    return element;
}

/**
 * Recursively loops through the elements in itemBody to look for interactions to replace and non matching languages to remove
 *
 * @param {Element} parentElement
 * @param {string} questionIdentifier
 * @param {string} language
 * @param {string} defaultLanguage
 */
export function getComponentNodes(parentElement, questionIdentifier, language, defaultLanguage) {
    Array.from(parentElement.childNodes).forEach((node) => {
        if (node.nodeType !== 1) {
            // non elements should be ignored
            return undefined;
        } else if (isInteraction(node)) {
            // interactions should be replaced by placeholders to be used as targets in the renderer
            return replaceInteractionWithPlaceholder(node, questionIdentifier);
        } else if (isPDFPanel(node)) {
            return parentElement.removeChild(node);
        } else if (isRubricBlock(node)) {
            return parentElement.removeChild(node);
        } else if (isMedia(node)) {
            // media elements should be replaced by placeholders to be used as targets in the renderer
            return replaceMediaWithPlaceholder(node, questionIdentifier);
        } else if (isInvalidEmptyInlineElement(node)) {
            // remove inline elements that are empty as they can wrap content
            return parentElement.removeChild(node);
        } else if (isInvalidHtmlMarkup(node)) {
            // remove blocks that cause harm like damaged interaction leftovers
            return parentElement.removeChild(node);
        } else if (!isInCorrectLanguage(node, language, defaultLanguage)) {
            // block elements in wrong language should be removed
            parentElement.removeChild(node);
            if (isInvalidEmptyInlineElement(parentElement)) {
                // after child removal, its parent might become empty. removing the parent if so.
                // asking his grandfather to do it ...
                parentElement.parentNode.removeChild(parentElement);
            }
            return undefined;
        }

        if (node.hasAttribute('lang') && node.hasAttribute('style')) {
            removeHiddenStyle(node);
        }

        if (isInsperaNamespacedStyleBodyElement(node)) {
            applyInsperaNamespaceStyle(node);
        }

        if (parentElement.hasChildNodes()) {
            return getComponentNodes(node, questionIdentifier, language, defaultLanguage);
        }

        return undefined;
    });
}

export function getInteractionElements(parentElement, language, defaultLanguage) {
    return Array
        .from(parentElement.childNodes)
        .reduce((accumulator, node) => {
            if (node.nodeType !== 1) {
                return accumulator;
            }

            if (isInteraction(node)) {
                accumulator.push(node);
                return accumulator;
            }

            if (isInCorrectLanguage(node, language, defaultLanguage)) {
                const elms = getInteractionElements(node);
                if (elms.length) {
                    accumulator.push(...elms);
                }
            }
            return accumulator;
        }, []);
}

export function htmlFromPlaceholderElement(element) {
    return serializer.serializeToString(element);
}

export function placeholderElementMapper(questionIdentifier) {
    return interactionElement => createPlaceholderElement(interactionElement, questionIdentifier, true);
}

export function getInteractionElementsAsHtml(parentElement, questionIdentifier, language, defaultLanguage) {
    return getInteractionElements(parentElement, language, defaultLanguage)
        .map(placeholderElementMapper(questionIdentifier))
        .map(htmlFromPlaceholderElement)
        .join('\n');
}

/**
 * Returns a light weight html from the itemBodyElement for the language provided. Content on other languages will be skipped.
 * The interactions will be replaced by place holders.
 * @param {string} questionIdentifier
 * @param {Element} itemBody
 * @param {string} language
 * @param {boolean} parseInteractionsOnly - optional
 * @returns {string}
 */
export function getItemBodyAsHTML(questionIdentifier, itemBody, language, parseInteractionsOnly, questionType) {
    const defaultLanguage = itemBody.getAttribute(constants.defaultLanguageAttr);
    const supportedLanguages = itemBody.getAttribute(constants.supportedLanguagesAttr);
    const languageIsSupported = supportedLanguages.includes(language);
    const langToUse = languageIsSupported ? language : undefined;
    // avoid having itemBody element as the root tag
    let bodyString = '';

    const isAudioScriptQuestion = questionType === objectTypes.AUDIO_SCRIPT;
    const isBorderHidden = (isAudioScriptQuestion && marketplacePropertiesService.shouldHideGridLinesInAudioScript());

    if (parseInteractionsOnly) {
        bodyString = getInteractionElementsAsHtml(itemBody, questionIdentifier, langToUse, defaultLanguage);
    } else {
        getComponentNodes(itemBody, questionIdentifier, langToUse, defaultLanguage);

        bodyString = Array
            .from(itemBody.childNodes)
            .reduce((str, node) => {
                return str.concat(serializer.serializeToString(node));
            }, '');
    }

    return `<div id="interactionWrapper_${questionIdentifier}"${isBorderHidden ? ' class="remove_border"' : ''}>${bodyString}</div>`;
}

export function replacePrintedVariables(
    itemBody,
    templateDeclarations,
    qtiData,
    isCalledFromFeedbackContext = false
) {
    const printedVariables = itemBody.getElementsByTagName('printedVariable');
    const domParser = new DOMParser();
    const missingIdentifier = '[MISSING_IDENTIFIER]';

    Array.from(printedVariables).forEach((variable) => {
        const identifier = variable.getAttribute('identifier');
        const className = variable.getAttribute('class');
        let template;

        if (className?.includes('system-variable')) {
            template = themeServices.getThemeInstance().getSystemPrintedVariable(identifier, qtiData);
        } else if (identifier) {
            const templateDeclaration = templateDeclarations.find(item => item.identifier === identifier);
            const effectiveValues = templateDeclaration?.value || templateDeclaration?.defaultValue;
            const notReplacedVariable = isCalledFromFeedbackContext ?
                t('questionFeedback.feedbackBody.unansweredInteraction') :
                `[${identifier}]`;
            template = (Array.isArray(effectiveValues) && effectiveValues.join('')) || notReplacedVariable;
        } else {
            template = missingIdentifier;
        }

        let parsedDom = domParser.parseFromString(template, 'text/xml');
        const containsATagButNotStartingWithOne = template.includes('</') && !template.trim().startsWith('<');
        if (containsATagButNotStartingWithOne || parsedDom?.childNodes?.length > 1) {
            // if not starting with a tag or more than one child, the template should be wrapped to make sure no data is missed
            parsedDom = domParser.parseFromString(`<span>${template}</span>`, 'text/xml');
        }
        const parsedTemplate = parsedDom?.firstChild;

        if (!parsedTemplate) {
            // in test mode DOMParser returns undefined if string is empty or not defined
            // just remove printedVariable if not parsable
            variable.parentNode.removeChild(variable);
            return;
        }

        variable.parentNode.replaceChild(parsedDom, variable);
    });
}
